import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Card,
  Container,
  Typography,
  Divider,
  Stack,
  Button,
  Chip,
  Alert,
  Breadcrumbs,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse,
} from "@mui/material";
import { HomeRounded, FingerprintOutlined, LockResetOutlined, HistoryOutlined, LogoutOutlined, ExpandMoreRounded, ExpandLessRounded } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Link } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import ApiCall from "../../utils/apicall";
import { showSuccessToast, showErrorToast } from "../../utils/Toast";
import { clearAccessToken } from "../../utils/authToken";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─────────────────────────────────────────────────────────────────────────────
export default function SecuritySettings() {
  const [credentials, setCredentials]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [registering, setRegistering]     = useState(false);
  const [platformAvailable, setPlatformAvailable] = useState(false);

  // ── Change Password ──
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const pwErr = (() => {
    if (pw.newPassword && pw.newPassword.length < 8) return "New password must be at least 8 characters.";
    if (pw.confirm && pw.newPassword !== pw.confirm) return "New password and confirmation do not match.";
    if (pw.newPassword && pw.currentPassword && pw.newPassword === pw.currentPassword) return "New password must be different from the current password.";
    return "";
  })();
  const pwValid = pw.currentPassword && pw.newPassword.length >= 8 && pw.newPassword === pw.confirm && !pwErr;
  const handleChangePassword = async () => {
    if (!pwValid) return;
    setPwSaving(true);
    try {
      await ApiCall.post("/auth/change-password", {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      showSuccessToast("Password changed successfully.");
      setPw({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to change password.");
    } finally {
      setPwSaving(false);
    }
  };

  // ── Sign-in history (admin-only, collapsible, lazy-loaded) ──
  const { loginUser } = useSelector((state) => state.userSlice);
  const sessionRoles = loginUser?.decodedToken?.roles || [];
  const isAdmin = sessionRoles.some((r) => ["Admin", "CEO"].includes(r));
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);
  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && !historyFetched) {
      setHistoryLoading(true);
      try {
        const res = await ApiCall.get("/auth/sign-in-history");
        setHistory(res.data?.data?.history ?? []);
      } catch { setHistory([]); }
      finally { setHistoryLoading(false); setHistoryFetched(true); }
    }
  };
  const fmtWhen = (d) => { try { return new Date(d).toLocaleString(); } catch { return String(d); } };
  const fmtAgent = (ua) => {
    if (!ua) return "Unknown device";
    if (/iPad/i.test(ua)) return "iPad";
    if (/iPhone/i.test(ua)) return "iPhone";
    if (/Android/i.test(ua)) return "Android";
    if (/Macintosh|Mac OS/i.test(ua)) return "Mac";
    if (/Windows/i.test(ua)) return "Windows";
    return "Browser";
  };

  // ── Sign out everywhere ──
  const [signingOutAll, setSigningOutAll] = useState(false);
  const handleSignOutEverywhere = async () => {
    setSigningOutAll(true);
    try {
      await ApiCall.post("/auth/sign-out-everywhere");
      showSuccessToast("Signed out of all devices. Redirecting to sign in...");
      setTimeout(() => { clearAccessToken(); window.location.href = "/login"; }, 1200);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to sign out everywhere.");
      setSigningOutAll(false);
    }
  };

  // Check if the device supports platform biometrics
  useEffect(() => {
    if (
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
    ) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPlatformAvailable)
        .catch(() => setPlatformAvailable(false));
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await ApiCall.get("/webauthn/credentials");
      setCredentials(res.data?.data ?? []);
    } catch {
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      // Step 1: Get registration options from server
      const optRes = await ApiCall.post("/webauthn/register/options");
      const options = optRes.data?.data;
      if (!options?.challenge) throw new Error("Failed to get registration options");

      // Step 2: Browser prompts biometric (Face ID / Touch ID / Windows Hello)
      const credential = await startRegistration(options);

      // Derive a human-readable label from attestation transport hints
      const transports = credential?.response?.transports ?? [];
      const label = transports.includes("internal")
        ? window.navigator.userAgent.includes("iPhone") || window.navigator.userAgent.includes("iPad")
          ? "iPhone / iPad Face ID"
          : window.navigator.userAgent.includes("Mac")
          ? "Mac Touch ID"
          : "Built-in Biometrics"
        : "Security Key";

      // Step 3: Verify with server
      await ApiCall.post("/webauthn/register/verify", { credential, label });
      showSuccessToast("Face ID / Biometric key registered successfully");
      fetchCredentials();
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        showErrorToast("Registration was cancelled");
      } else {
        showErrorToast(err?.response?.data?.message || err?.message || "Registration failed");
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (id, label) => {
    try {
      await ApiCall.delete(`/webauthn/credentials/${id}`);
      showSuccessToast(`Removed "${label}"`);
      fetchCredentials();
    } catch {
      showErrorToast("Failed to remove credential");
    }
  };

  return (
    <>
      <Helmet>
        <title>Security | SFM</title>
      </Helmet>

      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link to="/dashboard/app" style={{ display: "flex", alignItems: "center", color: "inherit", textDecoration: "none" }}>
            <HomeRounded fontSize="small" sx={{ mr: 0.5 }} /> Home
          </Link>
          <Typography color="text.primary">Security</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
          Security &amp; Sign-in
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
          Manage biometric sign-in options (Face ID, Touch ID, Windows Hello) for this account.
        </Typography>

        {/* ── Biometric Keys Card */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <FingerprintOutlined sx={{ color: "primary.main", fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Biometric Sign-in Keys
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Face ID (iPhone/iPad), Touch ID (Mac), Windows Hello, or Android fingerprint
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {!platformAvailable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This device or browser doesn&apos;t support biometric authentication. To register,
              open this page on your iPhone, iPad, Mac (Touch ID), or a Windows device with Windows Hello.
            </Alert>
          )}

          {/* Registered credentials list */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : credentials.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              No biometric keys registered yet.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {credentials.map((cred) => (
                <Stack
                  key={cred.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.neutral",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <FingerprintOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {cred.label || "Biometric key"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>
                        Registered{" "}
                        {new Date(cred.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </Typography>
                    </Box>
                    {cred.backedUp && (
                      <Chip label="Synced" size="small" color="success" variant="outlined" />
                    )}
                  </Stack>
                  <Tooltip title="Remove this key">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(cred.id, cred.label)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}

          {platformAvailable && (
            <Button
              variant="outlined"
              startIcon={registering ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
              disabled={registering}
              onClick={handleRegister}
              sx={{ mt: credentials.length > 0 ? 0 : 1 }}
            >
              {registering ? "Setting up..." : "Add Face ID / Biometric Key"}
            </Button>
          )}
        </Card>

        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <LockResetOutlined sx={{ color: "#F88F1D" }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Change Password</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Update your account password. You'll need your current password to confirm.
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2} sx={{ maxWidth: 440 }}>
            <TextField
              type="password" label="Current Password" fullWidth size="small"
              autoComplete="current-password" value={pw.currentPassword}
              onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })}
            />
            <TextField
              type="password" label="New Password" fullWidth size="small"
              autoComplete="new-password" value={pw.newPassword}
              onChange={(e) => setPw({ ...pw, newPassword: e.target.value })}
              helperText="At least 8 characters"
            />
            <TextField
              type="password" label="Confirm New Password" fullWidth size="small"
              autoComplete="new-password" value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              error={!!pwErr && !!pw.confirm} helperText={pw.confirm ? pwErr : ""}
            />
            <Box>
              <Button
                variant="contained" disabled={!pwValid || pwSaving}
                onClick={handleChangePassword}
                startIcon={pwSaving ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor: "#1D2330", "&:hover": { bgcolor: "#2A3340" }, textTransform: "none", fontWeight: 600 }}
              >
                {pwSaving ? "Updating..." : "Update Password"}
              </Button>
            </Box>
          </Stack>
        </Card>

        {isAdmin && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack
            direction="row" alignItems="center" spacing={1.5}
            onClick={toggleHistory}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            <HistoryOutlined sx={{ color: "#F88F1D" }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Recent Sign-ins
                <Chip label="Admin" size="small" sx={{ ml: 1, height: 18, fontSize: 11, fontWeight: 700, bgcolor: "#1D2330", color: "#fff" }} />
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                The last 25 sign-ins for your account, from the audit trail.
              </Typography>
            </Box>
            <IconButton size="small" aria-label={historyOpen ? "Collapse" : "Expand"}>
              {historyOpen ? <ExpandLessRounded /> : <ExpandMoreRounded />}
            </IconButton>
          </Stack>
          <Collapse in={historyOpen} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 2 }} />
            {historyLoading ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} /></Box>
            ) : history.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>No sign-in events recorded yet.</Typography>
            ) : (
              <TableContainer>
                <SmartTable tableId="security-sessions" lockedColumns={["When", "Device", "IP Address"]} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>{fmtWhen(h.createdAt)}</TableCell>
                        <TableCell>{fmtAgent(h.userAgent)}</TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>{h.ipAddress || "\u2014"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SmartTable>
              </TableContainer>
            )}
          </Collapse>
        </Card>
        )}

        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <LogoutOutlined sx={{ color: "#C0392B" }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Sign Out of All Devices</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Ends every active session for your account, including this one. Use this if a device was lost or you suspect unauthorized access.
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined" color="error" disabled={signingOutAll}
            onClick={handleSignOutEverywhere}
            startIcon={signingOutAll ? <CircularProgress size={16} color="inherit" /> : <LogoutOutlined />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {signingOutAll ? "Signing out..." : "Sign out everywhere"}
          </Button>
        </Card>

        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          Biometric keys are device-bound and processed locally — your biometric data never leaves
          your device. The system stores only a cryptographic public key.
        </Typography>
      </Container>
    </>
  );
}
